import React from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "hooks/useAuth"
import FormElement from "components/Form/FormElement"
import { jsonHash } from "utils/hash"
import {
    OCODE_GENDERS_LIST,
    ADMIN_BICYCODE,
    ADMIN_MFC,
    genderMFCList
 } from "utils/constants"

export const ServicesForm = ({
    partner,
    setUpdatedPartner,
    isMFCSelected,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    editCallback,
    showLocalErrors,
    setShowLocalErrors
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable: !isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

    const { t } = useTranslation()
    const { user } = useAuth()

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        if(partner) setFormFields(partner)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner])

    React.useEffect(() => {
        // When we create a new partner, we have to pass data to parent directly.
        // We don't have any submit button on this component in this case
        if(!isEdit) setUpdatedPartner((prevFormFields) => ({
            ...prevFormFields,
            mfc_client_number: formFields.mfc_client_number,
            gender_mfc: formFields.gender_mfc,
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, formFields])

    const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)
    const isAdminBicycode = user?.gender === ADMIN_BICYCODE
    const isAdminMFC = user?.gender === ADMIN_MFC

    const toggleEditableForm = (event) => {
        event.preventDefault()
        setFormEvents({
            ...formEvents,
            editable: !formEvents.editable
        })
    }

    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            setShowLocalErrors(false) // hide error

            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            const updatedPartner = {
                ...formFields,
                mfc_client_number: formFields.mfc_client_number,
                gender_mfc: formFields.gender_mfc,
            }

            await editCallback(updatedPartner)

            setFormEvents(s => ({
                ...s,
                ready: true,
                editable: false
            }))
        }
    }

    const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
        readonly: !formEvents.editable,
        required: false
	}

    return (
        <form className="">
            <section className="c-card p-5 md:p-12 mb-7">
                {(isAdminMFC || (isAdminOCode && formFields.isMFC) || isMFCSelected) &&
                    <section>
                        <h2 className="c-h2 font-semibold mb-7 uppercase">
                            {t("edit.provide_services", { ns: "partner" })} {isAdminOCode && " " + t("operators.mfc")}
                        </h2>
                        <div className="l-grid u-mg-negative-hz-m">
                            <div className="lg:w-1/2 w-full px-5">
                                {/* numéro série */}
                                <FormElement
                                    {...formElementCommonProps}
                                    value={formFields.mfc_client_number}
                                    name="mfc_client_number"
                                    type="text"
                                    label={t("edit.client_number", { ns: "partner" })}
                                    className="mb-5"
                                />
                            </div>
                            <div className="lg:w-1/2 w-full lg:pl-24 px-5">
                                {/* Type de partenaire */}
                                <FormElement
                                    {...formElementCommonProps}
                                    value={parseInt(formFields.gender_mfc)}
                                    name="gender_mfc"
                                    type="radio"
                                    options={genderMFCList}
                                    className=""
                                    classNameOptionsContainer="flex-col w-full"
                                    classNameLabel="mb-5"
                                    classNameOption="flex-1 items-center"
                                    label={t("specificity", { ns: "partner" })}
                                />
                            </div>
                        </div>
                    </section>
                }

                {isEdit && !isViewMode && !isAdminBicycode &&
                    <div className="flex justify-end mt-7">
                        {formEvents.editable
                            ?
                                <>
                                    <button
                                        type="button"
                                        className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
                                        onClick={toggleEditableForm}
                                    >
                                        {t("global.cancel")}
                                    </button>
                                    <button
                                        type="submit"
                                        className="c-btn c-btn--primary c-btn--m px-12 mx-5"
                                        onClick={handleSubmit}
                                        disabled={!formEvents.ready || !formHasChanged}
                                    >
                                        {t("global.save")}
                                    </button>
                                </>
                            :
                                <button
                                    type="button"
                                    className="c-btn c-btn--primary px-12 mx-5"
                                    onClick={toggleEditableForm}
                                >
                                    {t("global.update")}
                                </button>
                        }
                    </div>
                }
            </section>
        </form>
    )
}
